import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import ProfilePictureHover from '../../../components/ProfilePictureHover';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import {
  IcoUsers, EmployeeRole,
} from '../PayrollManagement';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export type OverrideMode = 'auto' | 'full' | 'custom_days';

export interface Employee {
  employee_id: string;
  employee_code?: string | null;
  user_id: string;
  name: string;
  email: string | null;
  gender: string;
  contact_no: string;
  qualification: string | null;
  employee_role: EmployeeRole;
  job_profile: string;
  profile_picture: string | null;
  has_salary: boolean;
}

export interface OverrideRow {
  employee_id: string;
  employee_code?: string | null;
  employee_role: EmployeeRole;
  employee_name: string;
  job_profile: string;
  mode: OverrideMode;
  deduct_days: string;
  custom_deduction: string;
  custom_deduction_reason: string;
}

interface EditableOverrideRow extends OverrideRow {
  _dirty: boolean;
  _errors: Record<string, string>;
  profile_picture: string | null;
  has_salary: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (overrides: OverrideRow[]) => void;
  initialOverrides: OverrideRow[];
  toast: React.RefObject<Toast | null>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const rowKey = (r: { employee_id: string; employee_role: EmployeeRole }) => `${r.employee_id}:${r.employee_role}`;

const isRowActive = (row: OverrideRow) =>
  row.mode !== 'auto' || (!!row.custom_deduction && Number(row.custom_deduction) > 0);

const toEditableRow = (emp: Employee, existing?: OverrideRow): EditableOverrideRow => {
  const base: OverrideRow = {
    employee_id: emp.employee_id,
    employee_code: emp.employee_code,
    employee_role: emp.employee_role,
    employee_name: emp.name,
    job_profile: emp.job_profile,
    mode: existing?.mode ?? 'auto',
    deduct_days: existing?.deduct_days ?? '',
    custom_deduction: existing?.custom_deduction ?? '',
    custom_deduction_reason: existing?.custom_deduction_reason ?? '',
  };
  return {
    ...base,
    _dirty: isRowActive(base),
    _errors: {},
    profile_picture: emp.profile_picture,
    has_salary: emp.has_salary,
  };
};

const validateRow = (row: OverrideRow): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (row.mode === 'custom_days') {
    if (!row.deduct_days || isNaN(+row.deduct_days) || +row.deduct_days < 0) {
      errors.deduct_days = 'Enter valid days';
    }
  }
  if (row.custom_deduction && (isNaN(+row.custom_deduction) || +row.custom_deduction < 0)) {
    errors.custom_deduction = 'Invalid amount';
  }
  return errors;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const PayrollOverridesModal: React.FC<Props> = ({ open, onClose, onApply, initialOverrides, toast }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [rows,      setRows]      = useState<EditableOverrideRow[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'overridden' | 'error' | 'no_salary'>('all');
  const [search,    setSearch]    = useState('');
  const [isClosing, setIsClosing] = useState(false);

  // ── Multi-select for bulk actions ─────────────────────────────────────────
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkMode,             setBulkMode]             = useState<OverrideMode>('full');
  const [bulkDeductDays,       setBulkDeductDays]       = useState('');
  const [bulkCustomDeduction,  setBulkCustomDeduction]  = useState('');
  const [bulkReason,           setBulkReason]           = useState('');

  // ── "Set mode for all" switch — applies instantly to every employee with a salary ──
  const [allMode, setAllMode]   = useState<OverrideMode | ''>('');
  const [allDays, setAllDays]   = useState('');

  // ── Fetch employees whenever the modal opens ──────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiClient.get('/payroll/employees')
      .then(res => {
        if (cancelled) return;
        if (res.data.status === 'success') setEmployees(res.data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load employees.', life: 3500 });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, toast]);

  // ── Build editable rows once employees are in, seeded from any prior overrides ──
  useEffect(() => {
    if (!open || employees.length === 0) return;
    const existingMap = new Map(initialOverrides.map(o => [rowKey(o), o]));
    setRows(employees.map(e => toEditableRow(e, existingMap.get(rowKey(e)))));
    setActiveFilter('all');
    setSearch('');
    setSelectedKeys(new Set());
    setAllMode('');
    setAllDays('');
    // initialOverrides intentionally excluded — only re-seed when the employee list itself refreshes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employees]);

  // ── Closing animation (same pattern as BulkEditStudentsModal) ─────────────
  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.payroll-overrides-modal .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.payroll-overrides-modal')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 400);
  };

  // ── Row mutation ───────────────────────────────────────────────────────────
  const updateRow = useCallback((key: string, patch: Partial<OverrideRow>) => {
    setRows(prev => prev.map(r => {
      if (rowKey(r) !== key) return r;
      const next: OverrideRow = { ...r, ...patch };
      const dirty = isRowActive(next);
      return { ...r, ...next, _dirty: dirty, _errors: dirty ? validateRow(next) : {} };
    }));
  }, []);

  const revertRow = useCallback((key: string) => {
    setRows(prev => prev.map(r => {
      if (rowKey(r) !== key) return r;
      return { ...r, mode: 'auto', deduct_days: '', custom_deduction: '', custom_deduction_reason: '', _dirty: false, _errors: {} };
    }));
  }, []);

  // ── Row selection (checkboxes, for bulk actions) ──────────────────────────
  const toggleRowSelected = useCallback((key: string) => {
    setSelectedKeys(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }, []);

  const toggleSelectAllVisible = useCallback((visibleKeys: string[]) => {
    setSelectedKeys(prev => {
      const allSelected = visibleKeys.length > 0 && visibleKeys.every(k => prev.has(k));
      if (allSelected) {
        const n = new Set(prev);
        visibleKeys.forEach(k => n.delete(k));
        return n;
      }
      return new Set([...prev, ...visibleKeys]);
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  // ── Bulk apply / reset ─────────────────────────────────────────────────────
  const applyBulk = () => {
    if (selectedKeys.size === 0) return;
    setRows(prev => prev.map(r => {
      if (!selectedKeys.has(rowKey(r))) return r;
      const next: OverrideRow = {
        ...r,
        mode: bulkMode,
        deduct_days: bulkMode === 'custom_days' ? bulkDeductDays : '',
        custom_deduction: bulkCustomDeduction,
        custom_deduction_reason: bulkReason,
      };
      const dirty = isRowActive(next);
      return { ...r, ...next, _dirty: dirty, _errors: dirty ? validateRow(next) : {} };
    }));
    toast.current?.show({ severity: 'success', summary: 'Applied', detail: `Override applied to ${selectedKeys.size} employee(s).`, life: 3000 });
    setSelectedKeys(new Set());
  };

  const resetSelectedToAuto = () => {
    if (selectedKeys.size === 0) return;
    const count = selectedKeys.size;
    setRows(prev => prev.map(r => {
      if (!selectedKeys.has(rowKey(r))) return r;
      return { ...r, mode: 'auto', deduct_days: '', custom_deduction: '', custom_deduction_reason: '', _dirty: false, _errors: {} };
    }));
    toast.current?.show({ severity: 'success', summary: 'Reset', detail: `${count} employee(s) reset to auto.`, life: 3000 });
    setSelectedKeys(new Set());
  };

  // ── Global mode switch — flips every salaried employee's mode at once ─────
  const applyModeToAll = (mode: OverrideMode) => {
    setAllMode(mode);
    setRows(prev => prev.map(r => {
      if (!r.has_salary) return r;
      const next: OverrideRow = { ...r, mode, deduct_days: mode === 'custom_days' ? r.deduct_days : '' };
      const dirty = isRowActive(next);
      return { ...r, ...next, _dirty: dirty, _errors: dirty ? validateRow(next) : {} };
    }));
    const label = mode === 'auto' ? 'Auto' : mode === 'full' ? 'Full salary' : 'Custom days';
    toast.current?.show({ severity: 'success', summary: 'Mode updated', detail: `All employees switched to ${label}.`, life: 3000 });
  };

  const applyDaysToAll = () => {
    if (!allDays) return;
    setRows(prev => prev.map(r => {
      if (!r.has_salary || r.mode !== 'custom_days') return r;
      const next: OverrideRow = { ...r, deduct_days: allDays };
      const dirty = isRowActive(next);
      return { ...r, ...next, _dirty: dirty, _errors: dirty ? validateRow(next) : {} };
    }));
    toast.current?.show({ severity: 'success', summary: 'Days updated', detail: `Deduct days set to ${allDays} for all custom-days employees.`, life: 3000 });
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const overriddenCount = rows.filter(r => r._dirty).length;
  const errorCount      = rows.filter(r => Object.keys(r._errors).length > 0).length;
  const noSalaryCount   = rows.filter(r => !r.has_salary).length;

  const visibleRows = rows.filter(r => {
    const matchesFilter =
      activeFilter === 'all'        ? true :
      activeFilter === 'overridden' ? r._dirty :
      activeFilter === 'no_salary'  ? !r.has_salary :
      Object.keys(r._errors).length > 0;

    const q = search.trim().toLowerCase();
    const matchesSearch = !q ||
      r.employee_name.toLowerCase().includes(q) ||
      r.job_profile.toLowerCase().includes(q) ||
      String(r.employee_id).includes(q) ||
      (r.employee_code ?? '').toLowerCase().includes(q);

    return matchesFilter && matchesSearch;
  });

  // ── Apply ─────────────────────────────────────────────────────────────────
  const handleApply = () => {
    if (errorCount > 0) {
      setActiveFilter('error');
      toast.current?.show({ severity: 'error', summary: 'Fix errors', detail: 'Some overrides have invalid values.', life: 3500 });
      return;
    }
    const active: OverrideRow[] = rows
      .filter(isRowActive)
      .map(({ _dirty, _errors, profile_picture, has_salary, ...rest }) => rest);
    onApply(active);
    handleClose();
  };

  // ── Column templates ──────────────────────────────────────────────────────

  const checkboxTemplate = (row: EditableOverrideRow) => (
    <input
      type="checkbox"
      checked={selectedKeys.has(rowKey(row))}
      onChange={() => toggleRowSelected(rowKey(row))}
      disabled={!row.has_salary}
      style={{ cursor: row.has_salary ? 'pointer' : 'not-allowed' }}
    />
  );

  const statusTemplate = (row: EditableOverrideRow) => {
    const hasErr = Object.keys(row._errors).length > 0;
    return (
      <div style={{
        width: 8, height: 8, borderRadius: '50%', margin: '0 auto',
        background: hasErr ? '#ef4444' : row._dirty ? '#f59e0b' : '#e2e8f0',
      }} />
    );
  };

  const employeeTemplate = (row: EditableOverrideRow) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <ProfilePictureHover
        src={row.profile_picture}
        name={row.employee_name}
        size={30}
        previewSize={200}
        borderRadius={8}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.employee_name}</div>
          {!row.has_salary && (
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
              background: 'rgba(239,68,68,.12)', color: '#dc2626', whiteSpace: 'nowrap',
              opacity: 1,
            }}>
              No salary
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>{row.job_profile} · #{row.employee_code ?? row.employee_id}</div>
      </div>
    </div>
  );

  const modeTemplate = (row: EditableOverrideRow) => (
    <select
      value={row.mode}
      onChange={e => updateRow(rowKey(row), { mode: e.target.value as OverrideMode })}
      disabled={!row.has_salary}
      style={{
        width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
        fontSize: 12, fontFamily: 'inherit', color: '#1e293b',
        background: row.has_salary ? 'white' : '#f3f4f6', outline: 'none', minWidth: 110,
        cursor: row.has_salary ? 'pointer' : 'not-allowed',
      }}
    >
      <option value="auto">Auto</option>
      <option value="full">Full salary</option>
      <option value="custom_days">Custom days</option>
    </select>
  );

  const deductDaysTemplate = (row: EditableOverrideRow) => {
    if (row.mode !== 'custom_days') return <span style={{ fontSize: 12, color: '#d1d5db' }}>—</span>;
    return (
      <div>
        <input
          type="number" min="0"
          disabled={!row.has_salary}
          style={{
            width: 80, padding: '5px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
            border: `1px solid ${row._errors.deduct_days ? '#ef4444' : '#e2e8f0'}`, outline: 'none',
            background: row.has_salary ? 'white' : '#f3f4f6',
          }}
          value={row.deduct_days}
          onChange={e => updateRow(rowKey(row), { deduct_days: e.target.value })}
        />
        {row._errors.deduct_days && <div style={{ color: '#ef4444', fontSize: 10, marginTop: 2 }}>{row._errors.deduct_days}</div>}
      </div>
    );
  };

  const customDeductionTemplate = (row: EditableOverrideRow) => (
    <div>
      <input
        type="number" min="0" placeholder="₹0"
        disabled={!row.has_salary}
        style={{
          width: 100, padding: '5px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
          border: `1px solid ${row._errors.custom_deduction ? '#ef4444' : '#e2e8f0'}`, outline: 'none',
          background: row.has_salary ? 'white' : '#f3f4f6',
        }}
        value={row.custom_deduction}
        onChange={e => updateRow(rowKey(row), { custom_deduction: e.target.value })}
      />
      {row._errors.custom_deduction && <div style={{ color: '#ef4444', fontSize: 10, marginTop: 2 }}>{row._errors.custom_deduction}</div>}
    </div>
  );

  const reasonTemplate = (row: EditableOverrideRow) => (
    <input
      type="text" placeholder="Optional"
      disabled={!row.has_salary}
      style={{
        width: 140, padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit', outline: 'none',
        background: row.has_salary ? 'white' : '#f3f4f6',
      }}
      value={row.custom_deduction_reason}
      onChange={e => updateRow(rowKey(row), { custom_deduction_reason: e.target.value })}
    />
  );

  const actionsTemplate = (row: EditableOverrideRow) => (
    <Button
      icon="pi pi-refresh"
      className="p-button-text p-button-rounded"
      tooltip="Reset to auto"
      tooltipOptions={{ position: 'top' }}
      disabled={!row._dirty || !row.has_salary}
      style={{ width: 28, height: 28, color: row._dirty && row.has_salary ? '#b45309' : '#d1d5db' }}
      onClick={() => revertRow(rowKey(row))}
    />
  );

  const selectAllHeaderTemplate = () => {
    const selectableVisible = visibleRows.filter(r => r.has_salary);
    return (
      <input
        type="checkbox"
        checked={selectableVisible.length > 0 && selectableVisible.every(r => selectedKeys.has(rowKey(r)))}
        onChange={() => toggleSelectAllVisible(selectableVisible.map(r => rowKey(r)))}
        disabled={selectableVisible.length === 0}
        style={{ cursor: selectableVisible.length > 0 ? 'pointer' : 'not-allowed' }}
      />
    );
  };

  // ── Header ────────────────────────────────────────────────────────────────

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div style={{
        width: 54, height: 54, borderRadius: 14,
        background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: 22, flexShrink: 0,
        border: '3px solid white', boxShadow: '0 8px 24px rgba(59,130,246,.3)',
      }}>
        <IcoUsers size={22} color="white" />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>Per-Employee Overrides</div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {loading ? 'Loading employees…' : `${employees.length} employees · everyone defaults to auto attendance`}
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      visible={open && !isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '98vw', maxWidth: 1100, borderRadius: 24, overflow: 'hidden' }}
      contentStyle={{
        padding: 18,
        background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
        maxHeight: 'calc(92vh - 120px)',
        overflowY: 'auto',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '20px 24px',
        borderRadius: '24px 24px 0 0',
      }}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      blockScroll
      maskClassName="payroll-overrides-modal"
    >
      <style>{`
        .payroll-overrides-modal { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.25)!important; animation:povMaskFadeIn .35s ease; }
        .payroll-overrides-modal.mask-closing { animation:povMaskFadeOut .4s ease!important; }
        @keyframes povMaskFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes povMaskFadeOut { from{opacity:1} to{opacity:0} }
        .payroll-overrides-modal .p-dialog { animation:povDialogOpen .35s cubic-bezier(.4,0,.2,1); box-shadow:0 25px 50px -12px rgba(0,0,0,.25)!important; }
        .payroll-overrides-modal .p-dialog.dialog-closing { animation:povDialogClose .4s cubic-bezier(.4,0,.2,1)!important; }
        @keyframes povDialogOpen { from{opacity:0;transform:scale(.92) translateY(-20px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes povDialogClose { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.88) translateY(25px)} }
        .pov-table .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:10px 8px!important; border-bottom:1px solid #e5e7eb!important; white-space:nowrap; }
        .pov-table .p-datatable-tbody > tr > td { padding:8px!important; border-bottom:1px solid #f3f4f6!important; vertical-align:middle; }
        .pov-table .p-datatable-tbody > tr.row-dirty { background:rgba(245,158,11,.04)!important; }
        .pov-table .p-datatable-tbody > tr.row-error { background:rgba(239,68,68,.04)!important; }
        .pov-table .p-datatable-tbody > tr.row-no-salary { background:#fafafa!important; opacity:.55; }
        .pov-table .p-datatable-tbody > tr.row-no-salary:hover { background:#fafafa!important; }
        .pov-table .p-datatable-tbody > tr:hover { background:rgba(15,118,110,.04)!important; }
        .pov-filter-pill { padding:5px 12px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid transparent; transition:all .15s; background:white; color:#64748b; border-color:#e2e8f0; }
        .pov-filter-pill.active-all        { background:#eff6ff; color:#3b82f6; border-color:#bfdbfe; }
        .pov-filter-pill.active-overridden { background:#fefce8; color:#ca8a04; border-color:#fde68a; }
        .pov-filter-pill.active-error      { background:#fef2f2; color:#dc2626; border-color:#fecaca; }
        .pov-filter-pill.active-no_salary  { background:#fff1f2; color:#e11d48; border-color:#fecdd3; }
        .pov-search { display:flex; align-items:center; gap:8px; background:white; border-radius:12px; padding:8px 14px; border:1px solid rgba(15,118,110,.15); box-shadow:0 1px 4px rgba(15,118,110,.06); }
        .pov-search input { flex:1; border:none; outline:none; font-size:13px; color:#1f2937; background:transparent; }
        .pov-search input::placeholder { color:#9ca3af; }
        .pov-mode-switch { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:10px 14px; border-radius:12px; background:#eff6ff; border:1px solid #bfdbfe; }
        .pov-mode-switch__label { font-size:12px; font-weight:700; color:#1e40af; white-space:nowrap; }
        .pov-mode-switch__group { display:flex; background:white; border-radius:10px; border:1px solid #bfdbfe; overflow:hidden; }
        .pov-mode-switch__btn { padding:6px 14px; font-size:12px; font-weight:600; color:#64748b; background:white; border:none; border-right:1px solid #e5e7eb; cursor:pointer; transition:all .15s; }
        .pov-mode-switch__btn:last-child { border-right:none; }
        .pov-mode-switch__btn.active { background:linear-gradient(135deg,#1e3a8a,#3b82f6); color:white; }
        .pov-mode-switch__btn:hover:not(.active) { background:#f1f5f9; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Global mode switch — sets mode for every salaried employee at once */}
        <div className="pov-mode-switch">
          <span className="pov-mode-switch__label">Set mode for all:</span>
          <div className="pov-mode-switch__group">
            <button
              className={`pov-mode-switch__btn ${allMode === 'auto' ? 'active' : ''}`}
              onClick={() => applyModeToAll('auto')}
            >
              Auto
            </button>
            <button
              className={`pov-mode-switch__btn ${allMode === 'full' ? 'active' : ''}`}
              onClick={() => applyModeToAll('full')}
            >
              Full salary
            </button>
            <button
              className={`pov-mode-switch__btn ${allMode === 'custom_days' ? 'active' : ''}`}
              onClick={() => applyModeToAll('custom_days')}
            >
              Custom days
            </button>
          </div>
          {allMode === 'custom_days' && (
            <>
              <input
                type="number" min="0" placeholder="Days"
                value={allDays}
                onChange={e => setAllDays(e.target.value)}
                style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }}
              />
              <Button label="Apply days to all" className="p-button-sm" style={{ background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)', border: 'none', fontSize: 12 }} onClick={applyDaysToAll} />
            </>
          )}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className={`pov-filter-pill ${activeFilter === 'all' ? 'active-all' : ''}`} onClick={() => setActiveFilter('all')}>
              All <span style={{ opacity: .7 }}>{rows.length}</span>
            </button>
            <button className={`pov-filter-pill ${activeFilter === 'overridden' ? 'active-overridden' : ''}`} onClick={() => setActiveFilter('overridden')}>
              <i className="pi pi-pencil" style={{ fontSize: 10, marginRight: 4 }} />
              Overridden <span style={{ opacity: .7 }}>{overriddenCount}</span>
            </button>
            {noSalaryCount > 0 && (
              <button className={`pov-filter-pill ${activeFilter === 'no_salary' ? 'active-no_salary' : ''}`} onClick={() => setActiveFilter('no_salary')}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: 10, marginRight: 4 }} />
                No salary set <span style={{ opacity: .7 }}>{noSalaryCount}</span>
              </button>
            )}
            {errorCount > 0 && (
              <button className={`pov-filter-pill ${activeFilter === 'error' ? 'active-error' : ''}`} onClick={() => setActiveFilter('error')}>
                <i className="pi pi-exclamation-circle" style={{ fontSize: 10, marginRight: 4 }} />
                Errors <span style={{ opacity: .7 }}>{errorCount}</span>
              </button>
            )}
          </div>

          <div className="pov-search" style={{ minWidth: 220 }}>
            <i className="pi pi-search" style={{ fontSize: 13, color: '#0f766e' }} />
            <input type="text" placeholder="Search name, ID, role…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0 2px' }}>
                <i className="pi pi-times" style={{ fontSize: 11 }} />
              </button>
            )}
          </div>
        </div>

        {/* Bulk action bar — shows once one or more employees are checked */}
        {selectedKeys.size > 0 && (
          <div style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(15,118,110,.05)', border: '1px solid rgba(15,118,110,.18)',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            animation: 'pr-fadeIn .3s ease',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f766e', whiteSpace: 'nowrap' }}>
              {selectedKeys.size} selected
            </span>

            <select
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }}
              value={bulkMode}
              onChange={e => setBulkMode(e.target.value as OverrideMode)}
            >
              <option value="full">Full salary</option>
              <option value="custom_days">Custom days</option>
            </select>

            {bulkMode === 'custom_days' && (
              <input
                type="number" min="0" placeholder="Days"
                style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }}
                value={bulkDeductDays}
                onChange={e => setBulkDeductDays(e.target.value)}
              />
            )}

            <input
              type="number" min="0" placeholder="₹ custom deduction"
              style={{ width: 140, padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }}
              value={bulkCustomDeduction}
              onChange={e => setBulkCustomDeduction(e.target.value)}
            />

            <input
              type="text" placeholder="Reason (optional)"
              style={{ width: 150, padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }}
              value={bulkReason}
              onChange={e => setBulkReason(e.target.value)}
            />

            <Button label={`Apply to ${selectedKeys.size}`} className="p-button-sm" style={{ background: 'linear-gradient(135deg,#0f766e,#0d9488)', border: 'none', fontSize: 12 }} onClick={applyBulk} />
            <Button label="Reset to auto" className="p-button-sm p-button-outlined" style={{ fontSize: 12 }} onClick={resetSelectedToAuto} />
            <button
              onClick={clearSelection}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, fontWeight: 600, marginLeft: 'auto' }}
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 11, color: '#94a3b8' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#e2e8f0', marginRight: 4 }} />Auto</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', marginRight: 4 }} />Overridden</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ef4444', marginRight: 4 }} />Has errors</span>
          <span style={{ marginLeft: 'auto' }}>{visibleRows.length} of {rows.length} shown</span>
        </div>

        {/* Table */}
        <div className="pov-table" style={{ background: 'white', borderRadius: 16, overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.05)' }}>
          <div style={{ overflowX: 'auto' }}>
            <DataTable
              value={visibleRows}
              loading={loading}
              rowClassName={(row: EditableOverrideRow) =>
                !row.has_salary ? 'row-no-salary' :
                Object.keys(row._errors).length > 0 ? 'row-error' : row._dirty ? 'row-dirty' : ''
              }
              emptyMessage={
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  <i className="pi pi-filter-slash" style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                  No employees match the current filter
                </div>
              }
              scrollable
              scrollHeight="440px"
            >
              <Column header={selectAllHeaderTemplate} body={checkboxTemplate} style={{ width: 32, textAlign: 'center' }} />
              <Column header="" body={statusTemplate} style={{ width: 28, textAlign: 'center' }} />
              <Column header="Employee" body={employeeTemplate} style={{ minWidth: 200 }} />
              <Column header="Mode" body={modeTemplate} style={{ minWidth: 130 }} />
              <Column header="Days to deduct" body={deductDaysTemplate} style={{ minWidth: 100 }} />
              <Column header="Custom deduction" body={customDeductionTemplate} style={{ minWidth: 120 }} />
              <Column header="Reason" body={reasonTemplate} style={{ minWidth: 150 }} />
              <Column header="" body={actionsTemplate} style={{ width: 44, textAlign: 'center' }} />
            </DataTable>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            {overriddenCount > 0
              ? <><span style={{ fontWeight: 700, color: '#b45309' }}>{overriddenCount}</span> employee(s) will use custom rules</>
              : 'No overrides — everyone uses auto attendance'}
            {errorCount > 0 && (
              <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}>· {errorCount} invalid</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              style={{ borderRadius: 10, fontSize: 13 }}
              onClick={handleClose}
            />
            <GlobalButton
              label={`Apply${overriddenCount > 0 ? ` (${overriddenCount})` : ''}`}
              onClick={handleApply}
              variant="primary"
              size="medium"
            />
          </div>
        </div>

      </div>
    </Dialog>
  );
};

export default PayrollOverridesModal;